import { theme } from '@theme';
import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PortfolioFilterType } from '../../../../../enums/PortfolioFilterType';
import { usePortfolioFilter } from '../../../../../hooks/UsePortfolioFilter';
import { AppState } from '../../../../../store/AppState';
import {
  setIsPortfolioFiltersChanged,
  setOpenFilterCount,
  updateSelectedFilters,
} from '../../../../../store/portfolio/PortfolioActions';
import { PortfolioState } from '../../../../../store/portfolio/PortfolioReducer';
import { DealerState } from '../../../../../types/DealerState';
import { FilterItemResource, MinMaxFilter } from '../../../../../types/FilterTypes';
import MultiSelectDropdown from '../../../../shared/filters/multi-select-dropdown/MultiSelectDropdown';
import { Typography } from '../../../../style';

type PortfolioInsightsFilterProps = {
  cssClass?: string;
};

const PortfolioInsightsFilter: FC<PortfolioInsightsFilterProps> = ({ cssClass }) => {
  const { dealType, openFilterCount } = useSelector<AppState, PortfolioState>(
    (state) => state.portfolio,
  );
  const { storeServices, featureToggle } = useSelector<AppState, DealerState>(
    (state) => state.dealer,
  );

  const { setSelectedFilter, getPortfolioView } = usePortfolioFilter();

  const currentView = getPortfolioView(dealType);
  if (currentView && (!storeServices.hasRecall || !featureToggle.recall)) {
    currentView.filters.insights = currentView?.filters.insights.filter(
      (x) => x.key != 'hasOpenRecall',
    );
  }
  const dispatch = useDispatch();

  const onOpen = () => {
    const newOpenFilterCount = openFilterCount + 1;
    dispatch(setOpenFilterCount(newOpenFilterCount));
  };
  const onClose = () => {
    const newOpenFilterCount = openFilterCount === 0 ? 0 : openFilterCount - 1;
    dispatch(setOpenFilterCount(newOpenFilterCount));
  };

  //if LBO is selected-
  //1) BPS range should default to 0-100
  //2) 'Updated' range should default to 0-9999
  const handleLBOSelection = (values: FilterItemResource[]) => {
    let bpsValue: MinMaxFilter<number> = { min: 0, max: 100 };
    let lastUpdated: MinMaxFilter<number> = { min: 0, max: 9999 };
    const selectedFilters = currentView.selectedFilters;
    const newSelectedFilters = { ...selectedFilters, bpsValue, lastUpdated };
    newSelectedFilters.bpsValue = bpsValue;
    newSelectedFilters.lastUpdated = lastUpdated;

    //update others filters
    newSelectedFilters.insights = values;
    dispatch(setIsPortfolioFiltersChanged(true, dealType));
    dispatch(updateSelectedFilters(newSelectedFilters, dealType));
  };
  const onPortfolioInsightsFilterChange = (values: FilterItemResource[]) => {
    let lboSelected = values.find((op) => op.key === 'isLeaseBuyout');
    if (lboSelected) {
      handleLBOSelection(values);
      return;
    } else {
      return setSelectedFilter(PortfolioFilterType.Insights, values, dealType);
    }
  };

  const label: string = currentView?.selectedFilters.insights?.length
    ? currentView?.selectedFilters.insights?.length > 1
      ? `${currentView?.selectedFilters.insights?.length} Selected`
      : currentView?.selectedFilters.insights[0].label
    : `All`;

  const getSubLabel = (): JSX.Element => {
    return (
      <Typography
        data-testid='dropdown-select-bottom-label'
        size={theme.typography.h5}
        color={theme.palette.white}
      >
        {label}
      </Typography>
    );
  };

  return (
    <div data-testid='portfolio-insights-filter' className={cssClass}>
      <MultiSelectDropdown
        placeholder={'Notes & Insights'}
        displaySelectedLabels={false}
        options={currentView?.filters.insights}
        onChange={(values) => onPortfolioInsightsFilterChange(values)}
        onUpdate={(values) =>
          setSelectedFilter(PortfolioFilterType.Insights, values, dealType, false)
        }
        values={currentView?.selectedFilters.insights}
        allowSearchFilter={false}
        displaySubLabel={getSubLabel()}
        enableCheckAll={false}
        onOpen={onOpen}
        onClose={onClose}
        type='am-label'
        cssClass='exclude-sorting'
        optionItemWidth={100}
        dynamicRender={false}
      />
    </div>
  );
};

export default PortfolioInsightsFilter;
